import { createContext, ReactNode, useContext, useMemo } from 'react'

/**
 * Instance Context Types
 *
 * Provides context for unified Platform and Team instance components.
 * This context is injected at the Layout level (TeamLayout / Layout) and
 * automatically available to all child components.
 */
export interface InstanceContextType {
  /**
   * Mode of operation
   * - 'platform': Personal/Platform instance view (all routes under Layout)
   * - 'team': Team instance view (all routes under TeamLayout)
   */
  mode: 'platform' | 'team'

  /**
   * Team resource ID (required when mode is 'team')
   * Injected from teamStore.teamTokenData?.teamResID in TeamLayout
   */
  teamResID?: string

  /**
   * Member resource ID (required when mode is 'team')
   * - Default: teamStore.scopeInfo?.resID (current logged-in user)
   * - Override: URL param from /team/member/:id (when admin views specific member)
   */
  memberResID?: string
}

export const InstanceContext = createContext<InstanceContextType | null>(null)

/**
 * Instance Context Provider
 *
 * Should be used in Layout components (TeamLayout / Layout) to provide
 * platform/team context to all child routes.
 *
 * @example
 * // In TeamLayout
 * <InstanceContextProvider
 *   mode="team"
 *   teamResID={teamStore.teamTokenData?.teamResID}
 *   memberResID={teamStore.scopeInfo?.resID}
 * >
 *   <Outlet />
 * </InstanceContextProvider>
 */
export function InstanceContextProvider({
  children,
  mode,
  teamResID,
  memberResID,
}: InstanceContextType & { children: ReactNode }) {
  const value = useMemo(
    () => ({
      mode,
      teamResID,
      memberResID,
    }),
    [mode, teamResID, memberResID],
  )

  return <InstanceContext.Provider value={value}>{children}</InstanceContext.Provider>
}

/**
 * Hook to consume Instance Context
 *
 * Throws error if used outside of InstanceContextProvider.
 * All instance-related components should use this hook to access context.
 *
 * @returns Instance context (guaranteed non-null)
 * @throws Error if used outside of InstanceContextProvider
 *
 * @example
 * function InstanceConfig() {
 *   const context = useInstanceContext()
 *   if (context.mode === 'team') {
 *     // Team-specific logic
 *   }
 * }
 */
export function useInstanceContext(): InstanceContextType {
  const ctx = useContext(InstanceContext)
  if (!ctx) {
    throw new Error('useInstanceContext must be used within InstanceContextProvider')
  }
  return ctx
}

/**
 * Instance Context Override
 *
 * Used to override context fields in specific routes.
 * Common use cases:
 * - Override memberResID: /team/member/:id where admin views a specific member's instances
 * - Override mode: /team/instances/member where member views own instances using platform APIs
 *
 * This creates a nested context that inherits parent context but overrides
 * specified fields.
 *
 * @example
 * // In MemberDetailPage (/team/member/:id) - Admin viewing specific member
 * function MemberDetailPage() {
 *   const { id } = useParams()
 *   return (
 *     <InstanceContextOverride memberResID={id}>
 *       <MemberInstancesTable />
 *     </InstanceContextOverride>
 *   )
 * }
 *
 * @example
 * // In MemberInstanceView (/team/instances/member) - Member viewing own instances
 * function MemberInstanceView() {
 *   return (
 *     <InstanceContextOverride mode="platform">
 *       <InstanceListView />
 *     </InstanceContextOverride>
 *   )
 * }
 */
export function InstanceContextOverride({
  mode,
  memberResID,
  children,
}: {
  mode?: 'platform' | 'team'
  memberResID?: string
  children: ReactNode
}) {
  const parent = useInstanceContext()
  const value = useMemo(
    () => ({
      ...parent,
      ...(mode !== undefined && { mode }),
      ...(memberResID !== undefined && { memberResID }),
    }),
    [parent, mode, memberResID],
  )

  return <InstanceContext.Provider value={value}>{children}</InstanceContext.Provider>
}

/**
 * Helper: Check if context is in team mode
 */
export function isTeamMode(ctx: InstanceContextType): boolean {
  return ctx.mode === 'team'
}

/**
 * Helper: Check if context is in platform mode
 */
export function isPlatformMode(ctx: InstanceContextType): boolean {
  return ctx.mode === 'platform'
}

/**
 * Helper: Validate team context has required fields
 */
export function isValidTeamContext(ctx: InstanceContextType): ctx is Required<InstanceContextType> {
  return isTeamMode(ctx) && !!ctx.teamResID && !!ctx.memberResID
}

/**
 * Helper: Get team context or throw error
 * Use this when you need guaranteed team context values
 */
export function requireTeamContext(ctx: InstanceContextType): Required<InstanceContextType> {
  if (!isValidTeamContext(ctx)) {
    throw new Error(
      `Team context required but invalid. mode=${ctx.mode}, teamResID=${ctx.teamResID}, memberResID=${ctx.memberResID}`,
    )
  }
  return ctx
}
